import { useState, FormEvent } from 'react';

interface AdminLoginPageProps {
  onLogin: (email: string, password: string) => Promise<boolean>;
  error: string;
}

export default function AdminLoginPage({ onLogin, error }: AdminLoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const success = await onLogin(email, password);
    setSubmitting(false);
    if (!success) {
      setShaking(true);
      setPassword('');
      setTimeout(() => setShaking(false), 500);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'linear-gradient(135deg, #fff8f2 0%, #f9f0e8 100%)' }}
    >
      {/* Decorative background circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, #5a9e6f 0%, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, #8b4513 0%, transparent 70%)' }}
        />
      </div>

      <div
        className={`relative w-full max-w-md bg-white rounded-2xl p-8 transition-all duration-300 ${
          shaking ? 'animate-[shake_0.4s_ease-in-out]' : ''
        }`}
        style={{ border: '1px solid #e8d8cc', boxShadow: '0 4px 32px rgba(90,70,50,0.08)' }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
            style={{ background: '#f2f8f4', border: '2px solid #c8e0cf' }}
          >
            <span className="flex items-center justify-center w-8 h-8">
              <i className="ri-lock-2-line text-2xl" style={{ color: '#5a9e6f' }}></i>
            </span>
          </div>
          <h1 className="text-xl font-bold mb-1" style={{ color: '#4a3728' }}>
            縁結び 和はは
          </h1>
          <p className="text-sm" style={{ color: '#9b8b7f' }}>
            管理画面へのアクセスには認証が必要です
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: '#4a3728' }}>
              メールアドレス
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
              style={{
                border: error ? '1.5px solid #cc4444' : '1.5px solid #e8d8cc',
                background: '#fdfaf8',
                color: '#4a3728',
                fontSize: '14px',
              }}
              autoFocus
              autoComplete="email"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: '#4a3728' }}>
              パスワード
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="パスワードを入力"
                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all pr-12"
                style={{
                  border: error ? '1.5px solid #cc4444' : '1.5px solid #e8d8cc',
                  background: '#fdfaf8',
                  color: '#4a3728',
                  fontSize: '14px',
                }}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
                style={{ color: '#9b8b7f' }}
              >
                <span className="flex items-center justify-center w-5 h-5">
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </span>
              </button>
            </div>
            {error && (
              <p className="mt-2 text-xs flex items-center gap-1" style={{ color: '#cc4444' }}>
                <span className="flex items-center justify-center w-3 h-3">
                  <i className="ri-error-warning-line"></i>
                </span>
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={!email || !password || submitting}
            className="w-full py-3 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-40 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            style={{ background: '#5a9e6f' }}
          >
            {submitting ? (
              <>
                <span className="flex items-center justify-center w-4 h-4">
                  <i className="ri-loader-4-line text-sm animate-spin"></i>
                </span>
                ログイン中...
              </>
            ) : (
              'ログイン'
            )}
          </button>
        </form>

        <p className="text-center text-xs mt-6" style={{ color: '#c4b4a8' }}>
          関係者専用ページです
        </p>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}